import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Hero from '../common/Hero';
import TimelineItem from '../features/TimelineItem';
import FamilyTree from '../features/FamilyTree';

const HistoryContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Section = styled.section`
  margin: 4rem 0;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 2rem;
  color: #333;
  font-family: 'Playfair Display', serif;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: #6c5ce7;
  }
`;

const Introduction = styled.div`
  margin-bottom: 3rem;
  
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #555;
  }
`;

const Timeline = styled.div`
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 100%;
    background-color: #f0f0f0;
  }
`;

const FamilyHistory = () => {
    const timelineEvents = [
        {
            id: 1,
            year: '1500–1700',
            title: 'Origins: The South-East Coast & AmaNguni Roots',
            description: 'The Mlalazi Clan, also known by its historical names Dumane, Mkhandela, Manxeba, and Ngalonde, originated in the south-eastern coastal belt of Africa near Mtunzini in present-day KwaZulu-Natal. Part of the greater Nguni ethnolinguistic group, they were skilled in blacksmithing, pastoralism, and early forms of governance. The clan’s identity, memory, and values were carried through oral traditions such as izibongo (praise poetry), forming the foundation of their cultural and historical continuity.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/nguni.jpg'
        },
        {
            id: 2,
            year: '1790–1840',
            title: 'uMfecane: Turmoil, Raids, and Exodus',
            description: 'The Mfecane upheaval led to massive displacement due to environmental pressure, overpopulation, and militarization of societies like the Zulu. Clans such as the Mlalazi were forcibly absorbed into emerging power blocs like King Mzilikazi’s breakaway group. Mlalazi warriors and artisans joined the expanding military forces, some as blacksmiths and spear-makers. They carried their distinct identity even as they merged with new communities.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/mfecane.jpg'
        },
        {
            id: 3,
            year: '1820s–1837',
            title: 'Regrouping and Rise of Mthwakazi',
            description: 'As King Mzilikazi’s forces moved north through Zeerust (Jwabheni), Botswana (Shoshong), and the Zambezi valley, the Ndebele nation formed. Mlalazi warriors were prominent in these travels. The clan helped build the AbeNhla group, comprised of people absorbed en route. They forged kinship through war, loyalty, and cultural assimilation, contributing military leadership and ironworking skills to the emerging state.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/mzilikazi.jpg'
        },
        {
            id: 4,
            year: '1838–1870',
            title: 'Establishment of uMhlahlandlela & Royal Politics',
            description: 'In Matabeleland, Mzilikazi built the capital uMhlahlandlela near the Khami River. Succession tension during his absence led to Prince Nkulumane’s temporary rule. On return, Mzilikazi executed dissenters and concealed his royal heirs. Mlalazi clansmen, including warriors like Mpucwane, continued to serve loyally. Lobengula was later installed in 1870. Mlalazis held elite military positions, participating in spear-throwing contests and regimental raids.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/ndebele.jpg'
        },
        {
            id: 5,
            year: '1870–1893',
            title: 'Mthwakazi Military Society & Cultural Order',
            description: 'Under King Lobengula, Ndebele society was highly organized. Military towns like ikhanda held thousands. Mlalazi members belonged to engineering regiments, makers of royal spears, and were praised in izibongo for their prowess: “Umlalazi owalol’ imkhonto yamakhosi.” They maintained settlements, guarded royal towns, and participated in cattle raids. The clan’s deep involvement is reflected in ceremonial, architectural, and educational systems defining the rhythm of everyday life.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/inyathi.jpg'
        },
        {
            id: 6,
            year: '1887–1894',
            title: 'Colonial Betrayals and Fall of the Nation',
            description: 'Treaties such as the Moffat Treaty and the Rudd Concession were manipulated by British settlers to seize mining rights. Lobengula’s refusal to be deceived led to conflict. Despite collecting rifles, the Ndebele army couldn’t compete with colonial guns. Lobengula’s capital was razed as he fled north. A Mlalazi warrior was said to have accompanied him beyond the Shangani River, possibly burying the king in secrecy and later re-emerging with a Tonga identity.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/lobengula.jpg'
        },
        {
            id: 7,
            year: 'Post-1894–Present',
            title: 'Scattering and Rebirth: A New Vision',
            description: 'After colonization, Mlalazi people dispersed across Zimbabwe, South Africa, and the Diaspora. Some adopted new names or languages to blend in. By the 21st century, clan elders led revival initiatives. A Clan Constitution was drafted to uphold values like integrity and respect. Regular Clan Gatherings began in 2011. Chapters were established in cities like Bulawayo and Johannesburg. The vision includes youth empowerment, cultural expression through dance, and strategic growth in business, education, and welfare.',
            image: 'https://dx8i2mtk03zrn.cloudfront.net/content/bulelani.jpg'
        }
    ];

    return (
        <>
            <Hero
                title="Our Clan History"
                subtitle="Tracing our roots through generations"
                backgroundImage="assets/images/logo.jpg"
            />

            <HistoryContainer>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <Introduction>
                        <SectionTitle>Our Story</SectionTitle>
                        <p>
                            For over a century, the Dumani clan has been weaving a rich tapestry of history, tradition, and accomplishment.
                            From humble beginnings to a sprawling clan network spanning continents, our journey is one of perseverance,
                            unity, and the enduring power of clan bonds. Explore the key moments that have shaped our collective identity
                            and the values that continue to guide us today.
                        </p>
                    </Introduction>

                    <Section>
                        <SectionTitle>Timeline</SectionTitle>
                        <Timeline>
                            {timelineEvents.map((event, index) => (
                                <TimelineItem
                                    key={event.id}
                                    year={event.year}
                                    title={event.title}
                                    description={event.description}
                                    image={event.image}
                                    alignment={index % 2 === 0 ? 'left' : 'right'}
                                />
                            ))}
                        </Timeline>
                    </Section>

                    {/** <Section>
                        <SectionTitle>Family Tree</SectionTitle>
                        <FamilyTree />
                    </Section>**/}
                </motion.div>
            </HistoryContainer>
        </>
    );
};

export default FamilyHistory;